import { useSuspenseQuery } from "@tanstack/react-query";
import { differenceInMonths, endOfMonth, startOfMonth } from "date-fns";
import { groupBy, orderBy, round, sum, uniqBy } from "lodash-es";
import { useState } from "react";
import { useNavigate } from "react-router";

import type { MonthlyReportDateRangeDto, TicketsBenchmarkItemDto } from "@/api/types";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { LegendDataIcon } from "@/components/Charts/LegendDataIcon";
import { dataColors } from "@/components/Charts/theme";
import { parseChartDate } from "@/components/Charts/util";
import { formatDuration } from "@/components/FormattedDuration/FormattedDuration";
import { NoData } from "@/components/NoData/NoData";
import { avgBy } from "@/helpers/math";
import { isDefined } from "@/helpers/util";
import { useProject } from "@/hooks/Network/useProject";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useQueryParams } from "@/hooks/useQueryParam";
import { useSlug } from "@/hooks/useSlug";
import { BarChart } from "@/modules/analytics/components/BarChart";
import type { AnalyticsRouteParams } from "@/modules/analytics/routes";
import type { DateRange } from "@/queries/analytics/queryOptions";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { CLOSED_TICKET_TAB } from "@/queries/tickets/constants";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import { AnalyticsCard } from "../../../components/AnalyticsCard";
import { type DateRangeOption, filterDataByDateRange } from "../../../util";

interface Props {
  dateRangeOptions: NonEmptyArray<DateRangeOption>;
}

const DEFAULT_DATE_RANGE: DateRange = "lastYear";

export function TicketHandlingTimeCard({ dateRangeOptions }: Props): React.ReactNode {
  const clockDate = useCurrentDate();
  const { t, i18n } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const slug = useSlug();
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = useQueryParams<AnalyticsRouteParams["overview"]>();

  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeOption>(dateRangeOptions[0]);
  const [category, setCategory] = useState<{ id: string; name: string }>();
  const { data } = useSuspenseQuery(analyticsQueries.ticketLeadTimeMonthly(DEFAULT_DATE_RANGE));
  const project = useProject();

  const handleBarClick = (range: MonthlyReportDateRangeDto, categoryId?: string) => {
    const params = new URLSearchParams({
      tab: CLOSED_TICKET_TAB,
      closedFrom: range.from.slice(0, 10),
      closedTo: range.to.slice(0, 10),
    });
    if (categoryId) {
      params.set("categoryIds", categoryId);
    }

    void navigate({ pathname: routes.tickets.list({ slug }), search: params.toString() });
  };

  const handleSelectDateRange = (item: DateRangeOption) => {
    setSelectedDateRange(item);

    // Remove the date from the query params when the selected date is changed manually
    setQueryParams({
      ...queryParams,
      "tickets-date-range": undefined,
    } satisfies AnalyticsRouteParams["overview"]);
  };

  let selectedDateRangeOverride = selectedDateRange;
  if (queryParams["tickets-date-range"]) {
    if (differenceInMonths(clockDate, new Date(queryParams["tickets-date-range"])) <= 6) {
      selectedDateRangeOverride = dateRangeOptions[0];
    } else if (differenceInMonths(clockDate, new Date(queryParams["tickets-date-range"])) <= 12) {
      selectedDateRangeOverride = dateRangeOptions[1];
    }
  }

  const categories = uniqBy(
    data.data.map((x) => ({ name: x.categoryName, id: x.categoryId })),
    (x) => x.id,
  );
  const benchmark = filterDataByDateRange(
    [
      ...data.benchmark,
      {
        range: {
          year: startOfMonth(clockDate).getFullYear(),
          month: startOfMonth(clockDate).getMonth() + 1,
          from: startOfMonth(clockDate).toISOString(),
          to: endOfMonth(clockDate).toISOString(),
        },
        averageRating: undefined,
        ticketCount: 0,
      } as TicketsBenchmarkItemDto,
    ].map((d) => ({
      x: parseChartDate(d.range).toISOString(),
      y: d.benchmarkDaysBeforeTicketClosed,
      range: d.range,
    })),
    selectedDateRangeOverride,
  );
  const primaryData = filterDataByDateRange(
    orderBy(Object.entries(groupBy(data.data, (x) => parseChartDate(x.range).toISOString())), (x) => x[0]).map(
      ([date, values]) => {
        const average = avgBy(
          values,
          (v) => v.averageDaysBeforeTicketClosed,
          (v) => v.closedTicketCount,
        );

        return {
          x: date,
          y: isDefined(average) ? round(average) : undefined,
          ticketCount: sum(values.map((x) => x.closedTicketCount)),
          isPending: values.every((x) => x.isCurrentMonth),
          range: values[0].range,
        };
      },
    ),
    selectedDateRangeOverride,
  );
  const groupedByCategory = Object.fromEntries(
    Object.entries(
      groupBy(
        data.data.map((d) => ({
          x: parseChartDate(d.range).toISOString(),
          y: d.averageDaysBeforeTicketClosed,
          categoryName: d.categoryName,
          categoryId: d.categoryId,
          ticketCount: d.closedTicketCount,
          isPending: d.isCurrentMonth,
          range: d.range,
        })),
        (d) => d.categoryId,
      ),
    ).map((x) => [x[0], filterDataByDateRange(x[1], selectedDateRangeOverride)]),
  );

  const noDataAvailable = data.data.every((x) => x.closedTicketCount === 0);

  return (
    <AnalyticsCard.Root variant="graph">
      <AnalyticsCard.Header
        title={t("page.analytics-overview.chart.avg-ticket-closing-time.title")}
        tooltip={t("page.analytics-overview.chart.avg-ticket-closing-time.title-tooltip")}
      >
        <AnalyticsCard.FilterSelect
          placeholder={t("page.analytics-overview.category-placeholder")}
          emptyItem={t("page.analytics-overview.category-deselect")}
          items={categories}
          selected={category}
          onChange={setCategory}
          renderOption={(x) => x.name}
          keySelector={(x) => x.id}
        />
        <AnalyticsCard.FilterSelect
          items={dateRangeOptions}
          selected={selectedDateRangeOverride}
          onChange={(item) => {
            handleSelectDateRange(item);
          }}
          renderOption={(x) => x.name}
          keySelector={(x) => x.value}
        />
      </AnalyticsCard.Header>
      {noDataAvailable ? (
        <NoData />
      ) : (
        <>
          <BarChart
            dataPrimary={{
              data: primaryData,
              onClick: (currDataPoint) => {
                handleBarClick(currDataPoint.range);
              },
            }}
            dataSecondary={
              category
                ? {
                    data: groupedByCategory[category.id],
                    onClick: (point) => handleBarClick(point.range, point.categoryId),
                  }
                : undefined
            }
            dataBenchmark={benchmark}
            xAxisType="date"
            formatYTick={(x) => formatDuration(i18n, { days: x }, ["days", "hours"], true)}
            heightAuto
            hoverLabel={(dataPoint, isSecondary) => (
              <table>
                <tbody>
                  <tr>
                    <td colSpan={3}>
                      {t("page.analytics-overview.chart.avg-ticket-closing-time.datapoint-label", {
                        amount: (isSecondary ? dataPoint.secondary?.ticketCount : dataPoint.primary?.ticketCount) || 0,
                      })}
                    </td>
                  </tr>
                  <tr>
                    <td className="pr-space-sm">
                      <LegendDataIcon fill={dataColors.primary} />
                    </td>
                    <td>{project.name}</td>
                    <td className="pl-space-sm text-caption-emphasis">
                      {formatDuration(i18n, { days: dataPoint.primary?.y }, ["days", "hours"], true)}
                    </td>
                  </tr>
                  {category && (
                    <tr>
                      <td className="pr-space-sm">
                        <LegendDataIcon fill={dataColors.green} />
                      </td>
                      <td>{category?.name || "-"}</td>
                      <td className="pl-space-sm text-caption-emphasis">
                        {formatDuration(i18n, { days: dataPoint.secondary?.y }, ["days", "hours"], true)}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="pr-space-sm">
                      <LegendDataIcon fill={dataColors.benchmark} type="minus" />
                    </td>
                    <td>{t("common.label.aop-benchmark")}</td>
                    <td className="pl-space-sm text-caption-emphasis">
                      {formatDuration(i18n, { days: dataPoint.benchmark?.y }, ["days", "hours"], true)}
                    </td>
                  </tr>
                  {dataPoint.primary?.isPending && (
                    <tr>
                      <td colSpan={4} className="text-caption text-text-default-inverse">
                        {t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          />
          <ChartLegend
            items={[
              {
                label: project.name,
                color: dataColors.primary,
              },
              ...(category
                ? [
                    {
                      label: category.name,
                      color: dataColors.green,
                    },
                  ]
                : []),
              {
                label: t("common.label.aop-benchmark"),
                color: dataColors.benchmark,
              },

              {
                label: t("page.analytics-details.engagement.chart.legend.current-period"),
                color: dataColors.primary,
                pattern: "striped",
              },
            ]}
          />
        </>
      )}
    </AnalyticsCard.Root>
  );
}
